import { type FC, useRef } from "react";
import { Col, Container, HeadingLevel, Layout } from "@viagogo/core/ui";
import { useFeatureParticipationRecorder, useFeatureTernary } from "@viagogo/feature";
import type { CityPageViewModel } from "src/http/types";
import { ContentPosition, ContentPositionType, FormatContent } from "src/shared/components/content";
import { RenderPositionalContent } from "src/shared/components/content/positional-content/PositionalContent";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { useScrollToggle } from "src/shared/hooks/useScrollToggle";
import type { Dates, Location } from "src/types/shared/cookies";
import { useCityPageModelData } from "src/website-apps/responsive/stubhub-city/hooks/useCityPageModelData";
import { NewImmersiveHeaderWrapper } from "src/website-apps/shared/components/ancillary-pages/NewImmersiveHeaderWrapper";
import { MarketplaceDisclosure } from "src/website-apps/shared/components/disclosures";
import { EmailSignUp } from "src/website-apps/shared/components/email-signup";
import { EventListRecentlyViewedGrid } from "src/website-apps/shared/components/event-list-recently-viewed-grid";
import { withExploreContexts } from "src/website-apps/shared/components/explore/withExploreContexts";
import { LazyFooter } from "src/website-apps/shared/components/footer";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { FavoritesProvider } from "src/website-apps/shared/components/grid/favorites-fs/FavoritesContext";
import { HeaderConfiguration } from "src/website-apps/shared/components/header";
import { MegaSearchProvider } from "src/website-apps/shared/components/instant-search-redesign-mega-search/MegaSearchProvider";
import { LinksGrid } from "src/website-apps/shared/components/LinksGrid";
import { SeoInformation } from "src/website-apps/shared/components/seo-content";
import { pageWrapperRecipe } from "src/website-apps/shared/styles/shared-styles";
import { ExploreOnCity } from "./ExploreOnCity";
import { VerticalMarginRow, VerticalMarginTopRow } from "./styled-components";

const resolveInitialFavoritesState = (model: CityPageViewModel) => {
  const events = [...(model.trendingEvents?.items ?? [])];
  const eventFavorites = Object.fromEntries(events.map((curr) => [curr.eventId ?? -1, curr.isFavorite]));
  return { [ENTITY_TYPES.EVENT]: { ...eventFavorites } };
};

interface Props {
  dates?: Maybe<Dates>;
  userLocation?: Maybe<Location>;
}

/**
 * Root Component for City Route
 */
const UnwrappedApp: FC<Props> = ({ dates, userLocation }) => {
  const model = useCityPageModelData();
  const initialFavorites = resolveInitialFavoritesState(model);
  const appContext = useAppContext();
  const scrollRef = useRef(null);
  const [mobileMenuOpen, handleOnMobileMenuToggle] = useScrollToggle();

  useFeatureParticipationRecorder(FeatureSetEnum.TMGlobalTrendingOnAncillaryPages)();

  const immersiveHeaderOnAncillary = useFeatureTernary(FeatureSetEnum.TMGlobalImmersiveHeaderOnAncillaryPages);

  const showImmersiveHeader = immersiveHeaderOnAncillary({
    control: () => false,
    variant: () => true,
    record: false,
  });

  return (
    <div className={pageWrapperRecipe({ mobileMenu: mobileMenuOpen ? "open" : "closed" })}>
      <FavoritesProvider data={initialFavorites}>
        <HeadingLevel>
          <Layout
            header={
              <MegaSearchProvider>
                <HeadingLevel>
                  <NewImmersiveHeaderWrapper headerTitle={model.cityName} dates={dates} location={userLocation}>
                    {!appContext.isNativeApp && (
                      <MarketplaceDisclosure marketplaceDisclosureSiteMessage={model.header.marketplaceDisclosure} />
                    )}
                    <HeaderConfiguration.Browse
                      discoveryLinks={model.header.navigationBarLinks}
                      isUserLoggedIn={appContext.isUserLoggedIn}
                      onMobileMenuToggle={handleOnMobileMenuToggle}
                      bigPerformerBanner={!!showImmersiveHeader}
                      mobileMenuOpen={mobileMenuOpen}
                    />
                  </NewImmersiveHeaderWrapper>
                </HeadingLevel>
              </MegaSearchProvider>
            }
            body={
              <Container ref={scrollRef}>
                <ExploreOnCity explore={model.explore} />
                {model.topVenues && model.topVenues.length > 0 && (
                  <LinksGrid links={model.topVenues} title={model.topVenuesTitle} />
                )}
                {model.citiesNearby && model.citiesNearby.length > 0 && (
                  <LinksGrid links={model.citiesNearby} title={model.citiesNearByTitle} />
                )}
                <EventListRecentlyViewedGrid />
                {model.hasSeoContent && (
                  <VerticalMarginTopRow>
                    <RenderPositionalContent
                      id={model.areaId}
                      type={ContentPositionType.MetroArea}
                      position={ContentPosition.StubhubGeoSeoBottomContent}
                    >
                      {(content) => (
                        <SeoInformation
                          content={content}
                          title={<FormatContent id="cid$#1912682#" params={[model.cityName]} />}
                          scrollRef={scrollRef}
                        />
                      )}
                    </RenderPositionalContent>
                  </VerticalMarginTopRow>
                )}
                <VerticalMarginRow>
                  {!appContext.isNativeApp && (
                    <Col span={[12]}>
                      <EmailSignUp emailPrivacyDisclosureSiteMessage={model.privacyPolicyMessage} />
                    </Col>
                  )}
                </VerticalMarginRow>
              </Container>
            }
            footer={
              <LazyFooter
                companyLinks={model.footer.companyLinks}
                supportLinks={model.footer.supportLinks}
                getLocationSettingsUrl={model.footer.getLocationSettingsUrl}
              />
            }
          />
        </HeadingLevel>
      </FavoritesProvider>
    </div>
  );
};

export const App = withExploreContexts(UnwrappedApp);
